import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function GestionPat() {
  const [patList, setPatList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPat, setEditingPat] = useState(null);
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [newKey, setNewKey] = useState('');
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    institution: '',
    password: '',
    carte_identite_base64: '',
    carte_identite_preview: null
  });

  // Utilisation de la configuration centralisée
  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');

  const fetchPat = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE}/admin/pat/lister.php`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      const data = await response.json();
      if (data.success) {
        setPatList(data.pat);
      } else if (data.message === 'Session expirée') {
        localStorage.removeItem('token');
        window.location.href = '/login';
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Erreur fetch:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Le fichier ne doit pas dépasser 5 Mo');
        return;
      }
      
      const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        toast.error('Formats acceptés: JPEG, PNG, WEBP');
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          carte_identite_base64: reader.result,
          carte_identite_preview: reader.result
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!editingPat && !formData.carte_identite_base64) {
      toast.error('La photo de la carte d\'identité est requise');
      return;
    }
    
    if (!formData.nom.trim()) {
      toast.error('Le nom est requis');
      return;
    }
    
    if (!formData.prenom.trim()) {
      toast.error('Le prénom est requis');
      return;
    }
    
    if (!formData.email.trim()) {
      toast.error('L\'email est requis');
      return;
    }
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Email invalide');
      return;
    }
    
    if (!formData.institution.trim()) {
      toast.error('L\'institution est requise');
      return;
    }
    
    if (!editingPat && (!formData.password || formData.password.length < 6)) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    const url = editingPat 
      ? `${API_BASE}/admin/pat/modifier.php`
      : `${API_BASE}/admin/pat/creer.php`;
    
    const body = editingPat 
      ? { 
          id_pat: editingPat.id_utilisateur,
          nom: formData.nom,
          prenom: formData.prenom,
          email: formData.email,
          telephone: formData.telephone,
          institution: formData.institution,
          password: formData.password
        }
      : formData;
    
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (data.success) {
        toast.success(editingPat ? 'PAT modifié avec succès' : 'PAT créé avec succès');
        if (!editingPat && data.cle_unique) {
          setNewKey(data.cle_unique);
          setShowKeyModal(true);
        }
        setShowForm(false);
        setEditingPat(null);
        resetForm();
        fetchPat();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur de connexion');
    }
  };

  const resetForm = () => {
    setFormData({
      nom: '',
      prenom: '',
      email: '',
      telephone: '',
      institution: '',
      password: '',
      carte_identite_base64: '',
      carte_identite_preview: null
    });
  };

  const supprimerPat = async (pat) => {
    if (!confirm(`⚠️ Êtes-vous sûr de vouloir supprimer définitivement le PAT "${pat.prenom} ${pat.nom}" ?\n\nCette action est irréversible.`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/admin/pat/supprimer.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getToken()}`
        },
        body: JSON.stringify({ id_pat: pat.id_utilisateur })
      });
      const data = await response.json();
      if (data.success) {
        toast.success(data.message);
        fetchPat();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Clé copiée dans le presse-papier');
  };

  const getCarteIdentiteUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${config.baseUrl}${path}`;
  };

  useEffect(() => {
    fetchPat();
  }, []);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-center">
          <div className="mx-auto h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Chargement des PAT...</p>
        </div>
      </div>
    );
  }

  const stats = {
    total: patList.length
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* En-tête */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Gestion des PAT</h1>
            <div className="mt-2">
              <span className="text-gray-600">📊 Total: <span className="font-semibold">{stats.total}</span></span>
            </div>
          </div>
          <button 
            onClick={() => { 
              setShowForm(true); 
              setEditingPat(null); 
              resetForm();
            }} 
            className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition shadow-md flex items-center gap-2"
          >
            <span className="text-lg">➕</span> Ajouter un PAT
          </button>
        </div>

        {/* Formulaire */}
        {showForm && (
          <div className="bg-white rounded-xl shadow-lg p-6 mb-6 border border-gray-100">
            <h2 className="text-xl font-bold text-gray-800 mb-5 pb-2 border-b">
              {editingPat ? '✏️ Modifier le PAT' : '➕ Nouveau PAT'}
            </h2>
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom *</label>
                <input 
                  type="text" 
                  placeholder="Nom" 
                  value={formData.nom} 
                  onChange={(e) => setFormData({ ...formData, nom: e.target.value })} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom *</label>
                <input 
                  type="text" 
                  placeholder="Prénom" 
                  value={formData.prenom} 
                  onChange={(e) => setFormData({ ...formData, prenom: e.target.value })} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email *</label>
                <input 
                  type="email" 
                  placeholder="exemple@email.com" 
                  value={formData.email} 
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Téléphone</label>
                <input 
                  type="tel" 
                  placeholder="XX XX XX XX XX" 
                  value={formData.telephone} 
                  onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Institution *</label>
                <input 
                  type="text" 
                  placeholder="Nom de l'institution" 
                  value={formData.institution} 
                  onChange={(e) => setFormData({ ...formData, institution: e.target.value })} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required 
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mot de passe {!editingPat && '*'}
                </label>
                <input 
                  type="password" 
                  placeholder={editingPat ? 'Nouveau mot de passe (optionnel)' : 'Mot de passe (min. 6 caractères)'} 
                  value={formData.password} 
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })} 
                  className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
                  required={!editingPat} 
                />
              </div>
              
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  🆔 Carte d'identité {!editingPat && '*'}
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition cursor-pointer"
                     onClick={() => document.getElementById('carteIdentiteInput').click()}>
                  <input 
                    id="carteIdentiteInput"
                    type="file" 
                    accept="image/jpeg,image/png,image/jpg,image/webp" 
                    onChange={handleFileChange} 
                    className="hidden"
                    required={!editingPat} 
                  />
                  {formData.carte_identite_preview ? (
                    <div>
                      <img src={formData.carte_identite_preview} alt="Aperçu carte" className="h-32 mx-auto border rounded shadow-sm" />
                      <p className="text-sm text-green-600 mt-2">✓ Image chargée</p>
                    </div>
                  ) : (
                    <div>
                      <div className="text-4xl mb-2">📄</div>
                      <p className="text-gray-500">Cliquez pour sélectionner la carte d'identité</p>
                      <p className="text-xs text-gray-400 mt-1">Formats: JPEG, PNG, WEBP (max 5 Mo)</p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="md:col-span-2 flex gap-3 pt-2">
                <button type="submit" className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition shadow">
                  {editingPat ? '✅ Modifier' : '✅ Créer'}
                </button>
                <button 
                  type="button" 
                  onClick={() => { 
                    setShowForm(false); 
                    setEditingPat(null); 
                    resetForm();
                  }} 
                  className="bg-gray-400 text-white px-6 py-2 rounded-lg hover:bg-gray-500 transition"
                >
                  ❌ Annuler
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Tableau des PAT */}
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Carte ID</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Nom complet</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Institution</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Clé d'activation</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {patList.map(pat => (
                  <tr key={pat.id_utilisateur} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4">
                      {pat.carte_identite_url ? (
                        <a 
                          href={pat.carte_identite_url} 
                          target="_blank" 
                          rel="noopener noreferrer" 
                          className="text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
                        >
                          🖼️ Voir
                        </a>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-semibold text-gray-800">{pat.prenom} {pat.nom}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{pat.email}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium">
                        {pat.institution || '-'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {pat.cle_unique ? (
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded" title={pat.cle_unique}>
                            {pat.cle_unique.substring(0, 20)}...
                          </span>
                          <button
                            onClick={() => copyToClipboard(pat.cle_unique)}
                            className="text-blue-600 hover:text-blue-800 text-sm"
                            title="Copier la clé"
                          >
                            📋
                          </button>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-6 py-4">
                      {pat.actif == 1 ? (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-semibold">✅ Actif</span>
                      ) : (
                        <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full text-xs font-semibold">⏳ En attente</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex gap-2">
                        <button 
                          onClick={() => { 
                            setEditingPat(pat); 
                            setFormData({ 
                              nom: pat.nom, 
                              prenom: pat.prenom, 
                              email: pat.email, 
                              telephone: pat.telephone || '', 
                              institution: pat.institution || '', 
                              password: '', 
                              carte_identite_base64: '', 
                              carte_identite_preview: null 
                            }); 
                            setShowForm(true); 
                          }} 
                          className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition"
                        >
                          ✏️ Modifier
                        </button>
                        <button 
                          onClick={() => supprimerPat(pat)} 
                          className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition"
                        >
                          🗑️ Supprimer
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {patList.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              <div className="text-5xl mb-3">📭</div>
              <p>Aucun PAT trouvé</p>
            </div>
          )}
        </div>

        {/* Modal pour afficher la clé générée */}
        {showKeyModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowKeyModal(false)}>
            <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
              <div className="text-center">
                <div className="text-6xl mb-4">🔑</div>
                <h2 className="text-xl font-bold text-gray-800 mb-2">Clé d'activation générée</h2>
                <p className="text-sm text-gray-600 mb-4">Voici la clé unique pour ce PAT :</p>
                <div className="bg-gray-100 p-3 rounded-lg font-mono text-sm break-all text-center mb-4 border">
                  {newKey}
                </div>
                <div className="flex gap-3">
                  <button 
                    onClick={() => copyToClipboard(newKey)} 
                    className="flex-1 bg-blue-600 text-white py-2 rounded-lg hover:bg-blue-700 transition"
                  >
                    📋 Copier
                  </button>
                  <button 
                    onClick={() => setShowKeyModal(false)} 
                    className="flex-1 bg-gray-400 text-white py-2 rounded-lg hover:bg-gray-500 transition"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}